import { map } from 'lodash'
import React, { useMemo } from 'react'
import { ColumnInstance } from 'react-table'
import { makeStyles } from '@material-ui/core'

import { contactSort } from 'core/table/cells/modern'
import { FIRST_LAST_NAME } from 'champion/utils/filters'
import { DYNAMIC_TEXT } from 'core/table/filters'
import { createStringSort } from 'core/table/utils'
import { CheckInTimeCell, LocalTimeCell } from '../components/cells'
import { createDateValueSort } from 'components/table/sort'
import { EditableCheckOut } from '../types'
import { ARRAY_OPERATOR_OPTIONS, STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'
import { CellWithTooltip, EditCell, EditHeader } from 'core/table/cells'
import { useOrganizationRoles } from 'tracking/activity/hooks/useOrganizationRoles'
import { ROLES } from 'tracking/activity/utils'

const useStyles = makeStyles(theme => ({
  opportunityColumn: {
    '&>div': {
      width: '160px',
    },
  },
  volunteerColumn: {
    '&>div': {
      width: '160px',
    },
  },
  checkInColumn: {
    '&>div': {
      width: '110px',
    },
  },
  checkOutColumn: {
    '&>div': {
      width: '80px',
    },
  },
  roleColumn: {
    '&>div': {
      width: '160px',
    },
  },
  editColumn: {
    '&>div': {
      width: '40px',
    },
  },
}))

export const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'opportunity':
    case 'volunteer':
      return STRING_OPERATOR_OPTIONS

    case 'role':
      return ARRAY_OPERATOR_OPTIONS

    default:
      return []
  }
}

export const useBulkCheckOutColumns = (onEdit: (row: EditableCheckOut) => void) => {
  const classes = useStyles()
  const { roles } = useOrganizationRoles()

  return useMemo(() => {
    return [
      {
        id: 'opportunity',
        Header: 'Opportunity',
        accessor: 'shift.name',
        Cell: CellWithTooltip,
        filter: DYNAMIC_TEXT,
        className: classes.opportunityColumn,
        sortType: createStringSort('opportunity.name'),
      },
      {
        id: 'volunteer',
        Header: 'Volunteer',
        accessor: (row: EditableCheckOut) => `${row.user.givenName} ${row.user.familyName}`,
        filter: FIRST_LAST_NAME,
        sortType: contactSort,
        className: classes.volunteerColumn,
      },
      {
        id: 'checkInTime',
        Header: 'Check-In Time',
        accessor: 'checkedInAt',
        Cell: CheckInTimeCell,
        disableFilters: true,
        sortType: createDateValueSort('checkedInAt'),
        className: classes.checkInColumn,
      },
      {
        id: 'checkOut',
        Header: 'Check-Out',
        accessor: 'checkedOutAt',
        Cell: LocalTimeCell,
        disableFilters: true,
        sortType: createDateValueSort('checkedOutAt'),
        className: classes.checkOutColumn,
      },
      {
        id: 'role',
        Header: 'Role',
        accessor: 'role.name',
        Cell: CellWithTooltip,
        filter: ROLES,
        className: classes.roleColumn,
        getFilterAutocomplete: (): string[] => map(roles, 'name'),
      },
      {
        id: 'actions',
        Header: <EditHeader hideDelete />,
        Cell: ({ cell }: any) => <EditCell cell={cell} onEdit={onEdit} />,
        disableSortBy: true,
        disableFilters: true,
        className: classes.editColumn,
      },
    ] as any as ColumnInstance<EditableCheckOut>[]
    // NOTE need to use `roles.length` instead of `roles` or causes update loop.
    // copied form useActivityLogColumns.tsx

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classes.opportunityColumn,
    classes.volunteerColumn,
    classes.checkInColumn,
    classes.checkOutColumn,
    classes.roleColumn,
    classes.editColumn,
    roles.length,
    onEdit,
  ])
}
